import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import inviteService from '../../services/inviteService';

/**
 * Invite Slice
 * 
 * Redux state management for invite system
 * Phase 4 M2: User Invite System
 */

// ==================== ASYNC THUNKS ====================

/**
 * Create invite (company owner)
 */
export const createInvite = createAsyncThunk(
  'invite/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await inviteService.createInvite(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch company invites (with pagination)
 */
export const fetchInvites = createAsyncThunk(
  'invite/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await inviteService.getInvites(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch invite statistics
 */
export const fetchInviteStats = createAsyncThunk(
  'invite/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await inviteService.getInviteStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch invite by ID
 */
export const fetchInviteById = createAsyncThunk(
  'invite/fetchById',
  async (inviteId, { rejectWithValue }) => {
    try {
      const response = await inviteService.getInviteById(inviteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Cancel invite
 */
export const cancelInvite = createAsyncThunk(
  'invite/cancel',
  async (inviteId, { rejectWithValue }) => {
    try {
      await inviteService.cancelInvite(inviteId);
      return inviteId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Resend invite
 */
export const resendInvite = createAsyncThunk(
  'invite/resend',
  async (inviteId, { rejectWithValue }) => {
    try {
      const response = await inviteService.resendInvite(inviteId);
      return { inviteId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Validate invite token (public)
 */
export const validateInviteToken = createAsyncThunk(
  'invite/validate',
  async (token, { rejectWithValue }) => {
    try {
      const response = await inviteService.validateInviteToken(token);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Accept invite (existing user)
 */
export const acceptInvite = createAsyncThunk(
  'invite/accept',
  async (token, { rejectWithValue }) => {
    try {
      const response = await inviteService.acceptInvite(token);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Reject invite (existing user)
 */
export const rejectInviteAction = createAsyncThunk(
  'invite/reject',
  async (token, { rejectWithValue }) => {
    try {
      const response = await inviteService.rejectInvite(token);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch user's pending invites
 */
export const fetchUserPendingInvites = createAsyncThunk(
  'invite/fetchUserPending',
  async (_, { rejectWithValue }) => {
    try {
      const response = await inviteService.getUserPendingInvites();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Register with invite (new user)
 */
export const registerWithInvite = createAsyncThunk(
  'invite/registerWithInvite',
  async ({ token, userData }, { rejectWithValue }) => {
    try {
      const response = await inviteService.registerWithInvite(token, userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ==================== INITIAL STATE ====================

const initialState = {
  // Company invites list
  invites: [],
  invitesTotal: 0,
  invitesPage: 1,
  invitesLimit: 10,
  invitesLoading: false,
  invitesError: null,

  // Invite statistics
  stats: {
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    expired: 0
  },
  statsLoading: false,
  statsError: null,

  // Current invite (for details/accept page)
  currentInvite: null,
  currentInviteLoading: false,
  currentInviteError: null,

  // User's pending invites
  userPendingInvites: [],
  userPendingLoading: false,
  userPendingError: null,

  // Create/update loading
  actionLoading: false,
  actionError: null
};

// ==================== SLICE ====================

const inviteSlice = createSlice({
  name: 'invite',
  initialState,
  reducers: {
    clearInvitesError: (state) => {
      state.invitesError = null;
    },
    clearCurrentInviteError: (state) => {
      state.currentInviteError = null;
    },
    clearActionError: (state) => {
      state.actionError = null;
    },
    clearCurrentInvite: (state) => {
      state.currentInvite = null;
      state.currentInviteError = null;
    }
  },
  extraReducers: (builder) => {
    // ==================== CREATE INVITE ====================
    builder
      .addCase(createInvite.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(createInvite.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(createInvite.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Davet gönderilemedi';
      });

    // ==================== FETCH INVITES ====================
    builder
      .addCase(fetchInvites.pending, (state) => {
        state.invitesLoading = true;
        state.invitesError = null;
      })
      .addCase(fetchInvites.fulfilled, (state, action) => {
        state.invitesLoading = false;
        state.invites = action.payload.invites;
        state.invitesTotal = action.payload.total;
        state.invitesPage = action.payload.page;
        state.invitesLimit = action.payload.limit;
      })
      .addCase(fetchInvites.rejected, (state, action) => {
        state.invitesLoading = false;
        state.invitesError = action.payload?.message || 'Davetler yüklenemedi';
      });

    // ==================== FETCH INVITE STATS ====================
    builder
      .addCase(fetchInviteStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchInviteStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchInviteStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload?.message || 'İstatistikler yüklenemedi';
      });

    // ==================== FETCH INVITE BY ID ====================
    builder
      .addCase(fetchInviteById.pending, (state) => {
        state.currentInviteLoading = true;
        state.currentInviteError = null;
      })
      .addCase(fetchInviteById.fulfilled, (state, action) => {
        state.currentInviteLoading = false;
        state.currentInvite = action.payload;
      })
      .addCase(fetchInviteById.rejected, (state, action) => {
        state.currentInviteLoading = false;
        state.currentInviteError = action.payload?.message || 'Davet bulunamadı';
      });

    // ==================== CANCEL INVITE ====================
    builder
      .addCase(cancelInvite.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(cancelInvite.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Update invite status in list
        const index = state.invites.findIndex(i => i._id === action.payload);
        if (index !== -1) {
          state.invites[index].status = 'cancelled';
        }
        // Update stats
        if (state.stats.pending > 0) {
          state.stats.pending -= 1;
        }
      })
      .addCase(cancelInvite.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Davet iptal edilemedi';
      });

    // ==================== RESEND INVITE ====================
    builder
      .addCase(resendInvite.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(resendInvite.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(resendInvite.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Davet tekrar gönderilemedi';
      });

    // ==================== VALIDATE INVITE TOKEN ====================
    builder
      .addCase(validateInviteToken.pending, (state) => {
        state.currentInviteLoading = true;
        state.currentInviteError = null;
      })
      .addCase(validateInviteToken.fulfilled, (state, action) => {
        state.currentInviteLoading = false;
        state.currentInvite = action.payload;
      })
      .addCase(validateInviteToken.rejected, (state, action) => {
        state.currentInviteLoading = false;
        state.currentInviteError = action.payload?.message || 'Davet doğrulanamadı';
      });

    // ==================== ACCEPT INVITE ====================
    builder
      .addCase(acceptInvite.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(acceptInvite.fulfilled, (state) => {
        state.actionLoading = false;
        // Clear current invite after successful accept
        state.currentInvite = null;
      })
      .addCase(acceptInvite.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Davet kabul edilemedi';
      });

    // ==================== REJECT INVITE ====================
    builder
      .addCase(rejectInviteAction.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(rejectInviteAction.fulfilled, (state) => {
        state.actionLoading = false;
        // Clear current invite after successful reject
        state.currentInvite = null;
      })
      .addCase(rejectInviteAction.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Davet reddedilemedi';
      });

    // ==================== FETCH USER PENDING INVITES ====================
    builder
      .addCase(fetchUserPendingInvites.pending, (state) => {
        state.userPendingLoading = true;
        state.userPendingError = null;
      })
      .addCase(fetchUserPendingInvites.fulfilled, (state, action) => {
        state.userPendingLoading = false;
        state.userPendingInvites = action.payload;
      })
      .addCase(fetchUserPendingInvites.rejected, (state, action) => {
        state.userPendingLoading = false;
        state.userPendingError = action.payload?.message || 'Davetler yüklenemedi';
      });

    // ==================== REGISTER WITH INVITE ====================
    builder
      .addCase(registerWithInvite.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(registerWithInvite.fulfilled, (state) => {
        state.actionLoading = false;
        // Clear current invite after successful registration
        state.currentInvite = null;
      })
      .addCase(registerWithInvite.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || 'Kayıt olunamadı';
      });
  }
});

// ==================== EXPORTS ====================

export const {
  clearInvitesError,
  clearCurrentInviteError,
  clearActionError,
  clearCurrentInvite
} = inviteSlice.actions;

export default inviteSlice.reducer;
